import { createContext, useCallback, useMemo } from 'react';
import { type AccountModalNames, ModalConversionPoints } from '@/components/Modals/shared-types';
import { recordConversion } from '@/utils/analytics/conversion';
import type { AccountModalsContextType, ModalProps } from '@/components/Modals/shared-types';

// Remap conversion points for tracking
export const conversionPoints: Record<ModalConversionPoints, string> = {
  [ModalConversionPoints.AccountPage]: 'premiumFeature_AccountPage',
  [ModalConversionPoints.AccountSidebar]: 'goPremium_accountSidebar',
  [ModalConversionPoints.AccountDownloads]: 'goPremium_accountDownloads',
  [ModalConversionPoints.AudioAd]: 'goPremium_uppbeatAa',
  [ModalConversionPoints.DownloadUpsell]: 'goPremium_downloadUpsell',
  [ModalConversionPoints.DownloadUpsellBusiness]: 'goPremium_downloadUpsellBusiness',
  [ModalConversionPoints.DownloadPremium]: 'goPremium_premiumTrack',
  [ModalConversionPoints.DownloadSfx]: 'goPremium_sfxDl',
  [ModalConversionPoints.DownloadLimit]: 'goPremium_downloadLimit',
  [ModalConversionPoints.External]: 'goPremium_external',
  [ModalConversionPoints.Header]: 'goPremium_header',
  [ModalConversionPoints.PricingPage]: 'goPremium_PricingPage',
  [ModalConversionPoints.SignUp]: 'goPremium_signUp',
  [ModalConversionPoints.HelpCenter]: 'goPremium_helpCenter',
  [ModalConversionPoints.DownloadPremiumMg]: 'goPremium_downloadPremiumMg',
  [ModalConversionPoints.ReturningUserLogin]: 'returningUserLogin',
  [ModalConversionPoints.UpgradeModal]: 'goPremium_upgradeModal',
  [ModalConversionPoints.SwitchToYearlyModal]: 'goPremium_switchToYearlyModal',
  // AuthDialog contextual triggers (see ModalConversionPoints comment).
  [ModalConversionPoints.AuthAddToBoard]: 'authDialog_addToBoard',
  [ModalConversionPoints.AuthPlaylistGenerator]: 'authDialog_playlistGenerator',
  [ModalConversionPoints.AuthPartnerApplication]: 'authDialog_partnerApplication',
  [ModalConversionPoints.DownloadFlowPremiumGate]: 'downloadFlow_premiumGate',
  [ModalConversionPoints.DownloadFlowSignUp]: 'downloadFlow_signUp',
  [ModalConversionPoints.DownloadFlowFirstDownload]: 'downloadFlow_firstDownload'
};
const ModalRoutingContext = createContext<{
  openModalWithConversionPoint: (modal: AccountModalNames, conversionPoint: ModalConversionPoints, modalProps?: ModalProps, screen?: string) => void;
} | null>(null);
interface ModalRoutingProviderProps {
  accountModalsContext: AccountModalsContextType;
  children: React.ReactNode;
}
export const ModalRoutingProvider = ({
  children,
  accountModalsContext
}: ModalRoutingProviderProps) => {
  const openModalWithConversionPoint = useCallback((modal: AccountModalNames, conversionPoint: ModalConversionPoints, modalProps?: ModalProps, screen?: string) => {
    accountModalsContext.setCurrentModal(modal, screen, modalProps);

    // Extract tag if it exists in modalProps for backward compatibility
    const tag = modalProps?.tag;
    recordConversion({
      source: conversionPoints[conversionPoint],
      assetId: tag == null ? undefined : Number(tag)
    });
  }, [accountModalsContext]);
  const value = useMemo(() => ({
    openModalWithConversionPoint
  }), [openModalWithConversionPoint]);
  return <ModalRoutingContext.Provider value={value} data-sentry-element="ModalRoutingContext.Provider" data-sentry-component="ModalRoutingProvider" data-sentry-source-file="ModalRoutingContext.tsx">
      {children}
    </ModalRoutingContext.Provider>;
};